import { Component, ChangeDetectionStrategy, signal, computed, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BaseComponent } from 'src/app/core/shared/common/base.component';

// Service Components
import { MedrServiceComponent } from './components/medr-service/medr-service.component';
import { MesServiceComponent } from './components/mes-service/mes-service.component';
import { MvssServiceComponent } from './components/mvss-service/mvss-service.component';
import { MxdrServiceComponent } from './components/mxdr-service/mxdr-service.component';
import { ProfessionalServicesComponent } from './components/professional-services/professional-services.component';
import { CustomerInfoComponent } from './components/customer-info/customer-info.component';
import { IncidentHandlingComponent } from './components/incident-handling/incident-handling.component';
import {
  OnboardingFormData,
  OnboardingStep,
  CustomerInfoData,
  IncidentHandlingData,
} from 'src/app/core/models/customer';

@Component({
  selector: 'app-customer-onboarding',
  standalone: true,
  templateUrl: './customer-onboarding.component.html',
  styleUrls: ['./customer-onboarding.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressBarModule,
    ButtonsModule,
    InputsModule,
    DropDownsModule,
    DateInputsModule,
    LabelModule,
    CustomerInfoComponent,
    MxdrServiceComponent,
    MedrServiceComponent,
    MvssServiceComponent,
    MesServiceComponent,
    ProfessionalServicesComponent,
    IncidentHandlingComponent,
  ],
})
export class CustomerOnboardingComponent extends BaseComponent {
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  // Form data - only store data for selected services
  public formData = signal<Partial<OnboardingFormData>>({
    // Only store incident handling data here
    // Service data will be stored separately per service
  });

  // Service configurations - only store data for selected services
  public serviceConfigurations = signal<{ [service: string]: any }>({});

  // Customer info data - separate signal for customer info from child component
  public customerInfoData = signal<CustomerInfoData | null>(null);

  public currentStep = signal('customer-info');
  public selectedServices = signal<string[]>([]);
  public serviceValidity = signal<{ [key: string]: boolean }>({});

  // Computed onboarding steps
  public onboardingSteps = computed(() => {
    const steps: OnboardingStep[] = [
      {
        id: 'customer-info',
        label: 'Customer Information',
        isCompleted: this.isCustomerInfoComplete(),
        isActive: this.currentStep() === 'customer-info',
        isRequired: true,
      },
    ];

    // Add service configuration steps for selected services
    const services = this.selectedServices();
    if (services.includes('mxdr')) {
      steps.push({
        id: 'mxdr-config',
        label: 'mXDR Configuration',
        isCompleted: this.isServiceComplete('mxdr'),
        isActive: this.currentStep() === 'mxdr-config',
        isRequired: true,
      });
    }
    if (services.includes('medr')) {
      steps.push({
        id: 'medr-config',
        label: 'mEDR Configuration',
        isCompleted: this.isServiceComplete('medr'),
        isActive: this.currentStep() === 'medr-config',
        isRequired: true,
      });
    }
    if (services.includes('mvss')) {
      steps.push({
        id: 'mvss-config',
        label: 'mVSS Configuration',
        isCompleted: this.isServiceComplete('mvss'),
        isActive: this.currentStep() === 'mvss-config',
        isRequired: true,
      });
    }
    if (services.includes('mes')) {
      steps.push({
        id: 'mes-config',
        label: 'mES Configuration',
        isCompleted: this.isServiceComplete('mes'),
        isActive: this.currentStep() === 'mes-config',
        isRequired: true,
      });
    }
    if (services.includes('professional')) {
      steps.push({
        id: 'professional-services-config',
        label: 'Professional Services',
        isCompleted: this.isServiceComplete('professional'),
        isActive: this.currentStep() === 'professional-services-config',
        isRequired: true,
      });
    }

    // Add incident handling and review steps
    steps.push({
      id: 'incident-handling',
      label: 'Incident Handling',
      isCompleted: this.isIncidentHandlingComplete(),
      isActive: this.currentStep() === 'incident-handling',
      isRequired: true,
    });

    steps.push({
      id: 'review-submit',
      label: 'Review & Submit',
      isCompleted: false,
      isActive: this.currentStep() === 'review-submit',
      isRequired: true,
    });

    return steps;
  });

  // Computed progress percentage - now based on navigation rather than form validation
  public progressPercentage = computed(() => {
    const steps = this.onboardingSteps();
    const currentIndex = this.currentStepIndex();

    // Calculate progress based on current step position
    // Each step represents equal progress
    if (steps.length === 0) return 0;

    // Progress is based on how many steps we've navigated through
    // First step (index 0) should show 0%, last step should show 100%
    // So we use currentIndex instead of currentIndex + 1
    const progressSteps = currentIndex;
    return Math.round((progressSteps / (steps.length - 1)) * 100);
  });

  // Computed current step index
  public currentStepIndex = computed(() => {
    const steps = this.onboardingSteps();
    return steps.findIndex((step) => step.id === this.currentStep());
  });

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Form initialization is now handled by individual components
  }

  public onStepClick(stepId: string): void {
    if (this.canNavigateToStep(stepId)) {
      this.currentStep.set(stepId);
    }
  }

  public onNext(): void {
    const steps = this.onboardingSteps();
    const currentIndex = this.currentStepIndex();

    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      this.currentStep.set(nextStep.id);
    }
  }

  public onPrevious(): void {
    const steps = this.onboardingSteps();
    const currentIndex = this.currentStepIndex();

    if (currentIndex > 0) {
      const previousStep = steps[currentIndex - 1];
      this.currentStep.set(previousStep.id);
    }
  }

  public onServiceDataUpdate(service: string, data: any): void {
    this.serviceConfigurations.update((current) => ({
      ...current,
      [service]: data,
    }));
  }

  public onServiceValidityUpdate(service: string, isValid: boolean): void {
    this.serviceValidity.update((current) => {
      const updated = {
        ...current,
        [service]: isValid,
      };
      return updated;
    });
  }

  public onCustomerInfoUpdate(data: CustomerInfoData): void {
    this.customerInfoData.set(data);
    // Sync selected services signal
    this.selectedServices.set(data.selectedServices);
  }

  public onSubmit(): void {
    if (this.isFormValid()) {
      // Submit the form data
      const submissionData = {
        customerInfo: this.customerInfoData(),
        serviceConfigurations: this.serviceConfigurations(),
        incidentHandling: this.formData().incidentHandlingData,
      };

      this.notificationService.show({
        content: 'Customer onboarding submitted successfully!',
        type: { style: 'success' },
        position: { horizontal: 'center', vertical: 'top' },
      });

      // Add a small delay to ensure notification is shown before navigation
      setTimeout(() => {
        this.router.navigate(['/customer/msp-portal/customers']);
      }, 1000);
    } else {
      this.notificationService.show({
        content: 'Please complete all required fields before submitting.',
        type: { style: 'error' },
        position: { horizontal: 'center', vertical: 'top' },
      });
    }
  }

  private isCustomerInfoComplete(): boolean {
    const customerInfo = this.customerInfoData();
    return !!(
      customerInfo?.customerName &&
      customerInfo?.friendlyName &&
      customerInfo?.preferredGoLiveDate &&
      this.selectedServices().length > 0
    );
  }

  private isIncidentHandlingComplete(): boolean {
    const incidentData = this.formData().incidentHandlingData;
    return !!incidentData?.incidentHandlingEmail;
  }

  private isServiceComplete(service: string): boolean {
    // Check if the service form is valid
    const isValid = this.serviceValidity()[service] || false;
    return isValid;
  }

  public canNavigateToStep(stepId: string): boolean {
    const steps = this.onboardingSteps();
    const targetIndex = steps.findIndex((step) => step.id === stepId);
    const currentIndex = this.currentStepIndex();

    // Can always go back
    if (targetIndex < currentIndex) {
      return true;
    }

    // Can only go forward if previous steps are completed
    for (let i = 0; i < targetIndex; i++) {
      if (!steps[i].isCompleted) {
        return false;
      }
    }

    return true;
  }

  public onCancel(): void {
    this.router.navigate(['/customer/msp-portal/customers']);
  }

  private checkFormValidity(): boolean {
    const steps = this.onboardingSteps();
    return steps.every((step) => step.isCompleted || step.id === 'review-submit');
  }

  public getSelectedServices(): string[] {
    return this.selectedServices();
  }

  public isCurrentStepValid(): boolean {
    const currentStepId = this.currentStep();

    switch (currentStepId) {
      case 'customer-info':
        const customerInfoValid = this.isCustomerInfoComplete();
        return customerInfoValid;
      case 'incident-handling':
        const incidentValid = this.isIncidentHandlingComplete();
        return incidentValid;
      case 'review-submit':
        const reviewValid = this.checkFormValidity();
        return reviewValid;
      default:
        // Service configuration step
        const service = this.getServiceKeyFromStepId(currentStepId);
        const serviceValid = this.isServiceComplete(service);
        return serviceValid;
    }
  }

  // Helper method to extract the correct service key from step ID
  private getServiceKeyFromStepId(stepId: string): string {
    switch (stepId) {
      case 'professional-services-config':
        return 'professional';
      default:
        return stepId.replace('-config', '');
    }
  }

  public onIncidentHandlingDataUpdate(data: IncidentHandlingData): void {
    this.formData.update((current) => ({
      ...current,
      incidentHandlingData: data,
    }));
  }

  public onIncidentHandlingValidityUpdate(isValid: boolean): void {
    this.serviceValidity.update((current) => ({
      ...current,
      incidentHandling: isValid,
    }));
  }

  public isFormValid(): boolean {
    const steps = this.onboardingSteps();
    return steps.every((step) => step.isCompleted || step.id === 'review-submit');
  }

  public getServiceData(service: string): any {
    return this.serviceConfigurations()[service] || null;
  }

  public getServiceDisplayName(service: string): string {
    const serviceMap: { [key: string]: string } = {
      mxdr: 'mXDR (Extended Detection & Response)',
      medr: 'mEDR (Endpoint Detection & Response)',
      mvss: 'mVSS (Vulnerability Scanning Service)',
      mes: 'mES (Email Security)',
      professional: 'Professional Services',
    };

    return serviceMap[service] || service;
  }
}
